<template>
  <div class="device-pairing-container">
    <el-card class="control-card">
      <div slot="header" class="clearfix">
        <span>🔍 设备配对控制台</span>
      </div>

      <!-- 配置表单 -->
      <el-form :model="formData" label-width="70px" label-position="right">
        <el-row :gutter="20" align="middle">
          <el-col :span="6">
            <el-form-item label="绑定台数">
              <el-input-number
                v-model="formData.deviceCount"
                :min="1"
                :max="100000"
                :step="1"
                :disabled="searching"
                style="width: 100%"
                placeholder="请输入绑定台数"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="搜索半径">
              <div style="display: flex; align-items: center; gap: 8px;">
                <el-input-number
                  v-model="formData.searchRadius"
                  :min="20"
                  :max="20000000"
                  :step="100"
                  :disabled="searching"
                  style="flex: 1"
                  placeholder="请输入搜索范围"
                />
                <span style="color: #666; white-space: nowrap;">米</span>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label-width="0px">
              <div style="display: flex; gap: 15px;">
                <el-button
                  v-if="!searching"
                  type="primary"
                  icon="el-icon-search"
                  :disabled="pairing || !wsConnected"
                  @click="startSearch"
                >
                  开启搜索
                </el-button>
                <el-button
                  v-else
                  type="danger"
                  icon="el-icon-close"
                  :disabled="pairing || !wsConnected"
                  @click="stopSearch"
                >
                  停止搜索
                </el-button>
                <el-button
                  type="success"
                  icon="el-icon-link"
                  :loading="pairing"
                  :disabled="!canConfirmBind || pairing || !wsConnected"
                  @click="confirmBind"
                >
                  {{ pairing ? '下发中...' : '确认绑定' }}
                </el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 定位信息 -->
    <el-card class="location-card" style="margin-top: 15px;">
      <div slot="header" class="clearfix">
        <span>📍 当前位置信息</span>
      </div>
      <div class="location-info">
        <div class="location-item">
          <span class="label">经度：</span>
          <span class="value">{{ currentLocation.lng || '未获取' }}</span>
        </div>
        <div class="location-item">
          <span class="label">纬度：</span>
          <span class="value">{{ currentLocation.lat || '未获取' }}</span>
        </div>
        <div class="location-item">
          <span class="label">精度：</span>
          <span class="value">{{ currentLocation.accuracy ? currentLocation.accuracy + '米' : '-' }}</span>
        </div>
        <div class="location-item">
          <span class="label">定位方式：</span>
          <span class="value">{{ currentLocation.locationType || '-' }}</span>
        </div>
        <div class="location-item">
          <span class="label">WebSocket：</span>
          <el-tag :type="wsConnected ? 'success' : 'danger'" size="small">
            {{ wsConnected ? '已连接' : '未连接' }}
          </el-tag>
        </div>
      </div>
    </el-card>

    <!-- WebSocket 连接状态提示 -->
    <el-card v-if="!wsConnected" class="status-card" style="margin-top: 15px;">
      <div slot="header" class="clearfix">
        <span>🔌 WebSocket 连接状态</span>
      </div>
      <div style="text-align: center; padding: 20px;">
        <i class="el-icon-loading" style="font-size: 32px; color: #409eff;"></i>
        <p style="margin-top: 10px;">正在连接 WebSocket 服务器...</p>
        <p style="font-size: 12px; color: #999;">请稍候，连接成功后即可使用设备配对功能</p>
      </div>
    </el-card>

    <!-- 设备列表 -->
    <el-card class="device-list-card">
      <div slot="header" class="clearfix">
        <span>📱 搜索到的设备列表</span>
        <el-tag type="info" size="small" style="float: right;">
          共 {{ discoveredDevices.length }} 台设备
        </el-tag>
      </div>
      <div class="table-wrapper">
        <el-table
          :data="discoveredDevices"
          v-loading="searching && discoveredDevices.length === 0"
          border
          stripe
          height="36vh"
          :row-style="{height: '55px'}"
          :cell-style="{padding: '0'}"
        >
          <el-table-column
            label="序号"
            type="index"
            :index="indexMethod"
            width="60"
            align="center"
            fixed="left"
          />
          <el-table-column prop="deviceId" label="设备ID" min-width="150" align="center" show-overflow-tooltip />
          <el-table-column prop="batchNo" label="批次号" min-width="150" align="center" show-overflow-tooltip />
          <el-table-column prop="province" label="省份" min-width="100" align="center" show-overflow-tooltip />
          <el-table-column label="位置" min-width="200" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <span v-if="scope.row.gps && scope.row.gps.lng && scope.row.gps.lat">
                {{ Number(scope.row.gps.lng).toFixed(6) }}, {{ Number(scope.row.gps.lat).toFixed(6) }}
              </span>
              <span v-else-if="scope.row.lng && scope.row.lat">
                {{ Number(scope.row.lng).toFixed(6) }}, {{ Number(scope.row.lat).toFixed(6) }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="距离（米）" width="110" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.distance ? scope.row.distance.toFixed(1) : '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="报警电压等级" width="120" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.voltageLevel || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="设备状态" width="90" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.deviceStatus)" size="small">
                {{ getStatusText(scope.row.deviceStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="联机状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag :type="getOnlineStatusTagType(scope.row.onlineStatus)" size="small">
                {{ getOnlineStatusText(scope.row.onlineStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="配对状态" width="90" align="center">
            <template slot-scope="scope">
              <el-tag :type="getPairStatusTagType(scope.row.pairStatus)" size="small">
                {{ getPairStatusText(scope.row.pairStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="分组ID" min-width="200" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              <span :class="{ 'text-success': scope.row.groupId }">
                {{ scope.row.groupId || '未分组' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="最后更新" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.timestamp) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script>
import WebSocketService from '@/components/websocket/webSocketService'
import { wgs84ToGcj02 } from '@/components/gps/gpsCorrect'
import { mapGetters } from 'vuex'

export default {
  name: 'DevicePairing',
  data() {
    return {
      formData: {
        deviceCount: 5,
        searchRadius: 100
      },
      currentLocation: {
        lng: null,
        lat: null,
        accuracy: null,
        locationType: null
      },
      discoveredDevices: [],
      searching: false,
      pairing: false,
      wsConnected: false,
      searchTimer: null,
      messageHandler: null,
      wsService: null,
      statusCheckInterval: null,
      currentRequestId: null
    }
  },
  computed: {
    ...mapGetters('globalUserInfo', [
      'isAdmin',
      'isNormalAdmin',
      'userAccount',
      'userId'
    ]),
    hasPairingPermission() {
      return this.isAdmin === true
    },
    canConfirmBind() {
      return this.discoveredDevices.length >= this.formData.deviceCount &&
        this.discoveredDevices.length > 0 &&
        !this.searching &&
        !this.pairing
    }
  },
  mounted() {
    this.checkPermission()
    this.initWebSocket()
  },
  beforeDestroy() {
    this.stopAllTimers()
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval)
      this.statusCheckInterval = null
    }
    if (this.messageHandler && this.wsService) {
      this.wsService.offMessage(this.messageHandler)
    }
  },
  methods: {
    indexMethod(index) {
      return index + 1
    },

    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    checkPermission() {
      if (!this.hasPairingPermission) {
        this.$alert(
          '<div style="line-height: 1.6;">' +
          '<p style="font-size: 16px; color: #e6a23c; margin-bottom: 12px;">⚠️ 权限不足</p>' +
          '<p>您没有使用设备配对功能的权限。</p>' +
          '<p style="margin-top: 12px;"><strong>设备配对功能仅限以下角色使用：</strong></p>' +
          '<p>• 超级管理员 (Super Admin)</p>' +
          '<p style="margin-top: 12px; color: #909399; font-size: 13px;">如需使用此功能，请联系系统管理员开通权限。</p>' +
          '</div>',
          '无法使用设备配对',
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '我知道了',
            type: 'warning'
          }
        )
      }
    },

    showPermissionGuide() {
      this.$confirm(
        '<div style="line-height: 1.6;">' +
        '<p style="font-size: 16px; color: #f56c6c; margin-bottom: 12px;">⛔ 权限不足</p>' +
        '<p>您没有使用设备配对功能的权限！</p>' +
        '<p style="margin-top: 12px;"><strong>设备配对功能仅限管理员使用：</strong></p>' +
        '<p>• 超级管理员 (Super Admin)</p>' +
        '<p style="margin-top: 12px; padding: 8px; background: #f5f7fa; border-radius: 4px;">' +
        '<strong>🔧 解决方法：</strong><br/>' +
        '请联系管理员开通设备配对权限' +
        '</p>' +
        '<p style="margin-top: 12px; color: #909399; font-size: 12px;">' +
        '当前账号：' + (this.userAccount || '未知') +
        '</p>' +
        '</div>',
        '权限不足',
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '知道了',
          showCancelButton: false,
          type: 'warning'
        }
      )
    },

    showLocationPermissionGuide() {
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
      const isFirefox = /Firefox/.test(navigator.userAgent)
      const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
      const isEdge = /Edg/.test(navigator.userAgent)

      let guideText = ''
      if (isChrome || isEdge) {
        guideText = 'Chrome/Edge浏览器：点击地址栏左侧的锁图标 → 位置权限 → 允许，然后刷新页面'
      } else if (isFirefox) {
        guideText = 'Firefox浏览器：点击地址栏左侧的锁图标 → 权限 → 位置 → 允许，然后刷新页面'
      } else if (isSafari) {
        guideText = 'Safari浏览器：设置 → 网站 → 位置 → 允许，然后刷新页面'
      } else {
        guideText = '请在浏览器设置中开启定位权限，然后刷新页面'
      }

      this.$confirm(
        '<div style="line-height: 1.6;">' +
        '<p style="font-size: 16px; color: #e6a23c; margin-bottom: 12px;">📍 需要获取您的位置</p>' +
        '<p>设备配对功能需要获取您的地理位置，以便搜索附近的设备。</p>' +
        '<p style="margin-top: 12px;"><strong>🔧 开启方法：</strong></p>' +
        '<p style="color: #409eff; background: #f5f7fa; padding: 8px; border-radius: 4px;">' + guideText + '</p>' +
        '<p style="margin-top: 12px; font-size: 12px; color: #999;">开启后请重新点击"开启搜索"按钮。</p>' +
        '</div>',
        '定位权限被拒绝',
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '知道了',
          showCancelButton: false,
          type: 'warning'
        }
      )
    },

    initWebSocket() {
      const self = this
      this.wsService = new WebSocketService()
      this.registerMessageHandler()

      this.statusCheckInterval = setInterval(() => {
        if (self.wsService) {
          const connected = self.wsService.isConnected
          if (connected !== self.wsConnected) {
            self.wsConnected = connected
          }
        }
      }, 500)
    },

    registerMessageHandler() {
      if (!this.wsService) return

      if (this.messageHandler) {
        this.wsService.offMessage(this.messageHandler)
      }

      this.messageHandler = (data) => {
        this.handleWebSocketMessage(data)
      }
      this.wsService.onMessage(this.messageHandler)
    },

    handleWebSocketMessage(data) {
      try {
        console.log('收到WebSocket消息:', data)

        // 处理搜索结果
        if (data.type === 'device_search_response' && data.devices) {
          this.updateDiscoveredDevices(data.devices)

          // 达到目标数量，自动停止
          if (this.searching && this.discoveredDevices.length >= this.formData.deviceCount) {
            this.$message.success(`已找到${this.discoveredDevices.length}台设备，满足绑定条件，自动停止搜索`)
            this.stopSearch(true)
          }
        }

        // 处理搜索完成
        if (data.type === 'search_completed') {
          if (this.searching) {
            if (data.totalDevices > 0) {
              this.$message.info(`搜索完成，共发现 ${data.totalDevices} 台设备`)
            } else {
              this.$message.info('搜索完成，未找到附近设备')
            }
          }
          this.searching = false
          this.currentRequestId = null
          this.stopAllTimers()
        }

        // 处理搜索停止
        if (data.type === 'device_search_response' && data.action === 'stopped') {
          this.searching = false
          this.currentRequestId = null
          this.stopAllTimers()
        }

        // 处理设备状态更新（包括配对状态变化）
        if (data.deviceId && data.pairStatus !== undefined) {
          this.updateDeviceStatus(data)

          // 检查是否有设备配对状态变为已配对
          const device = this.discoveredDevices.find(d => d.deviceId === data.deviceId)
          if (device && device.pairStatus === '0' && device.groupId) {
            this.$message.success(`设备 ${device.deviceId} 已成功配对！分组ID: ${device.groupId}`)
          }
        }

        // 处理绑定结果（只是创建分组并开始下发，不是最终成功）
        if (data.type === 'bind_result') {
          this.pairing = false
          if (data.success) {
            this.$message.info(`已创建分组 ${data.groupId}，正在下发分组ID给 ${data.deviceCount} 台设备，等待设备确认...`)
          } else {
            this.$message.error(`创建分组失败：${data.message || '未知错误'}`)
            this.searching = false
            this.currentRequestId = null
            this.stopAllTimers()
          }
        }

        // 处理配对完成（所有设备都确认了）
        if (data.type === 'pairing_completed') {
          this.$message.success(`所有设备已确认！配对完成！分组 ${data.groupId}`)
          this.searching = false
          this.currentRequestId = null
          this.discoveredDevices = []
          this.stopAllTimers()
        }

        // 处理错误
        if (data.type === 'error') {
          this.$message.error(data.message || '操作失败')
          if (data.action === 'search') {
            this.searching = false
            this.currentRequestId = null
            this.stopAllTimers()
          }
        }

      } catch (e) {
        console.error('处理消息失败:', e)
      }
    },

    updateDiscoveredDevices(newDevices) {
      const existingIds = new Set(this.discoveredDevices.map(d => d.deviceId))

      newDevices.forEach(device => {
        if (existingIds.has(device.deviceId)) {
          const index = this.discoveredDevices.findIndex(d => d.deviceId === device.deviceId)
          if (index !== -1) {
            this.$set(this.discoveredDevices, index, {
              ...this.discoveredDevices[index],
              ...device,
              timestamp: Date.now()
            })
          }
        } else {
          this.discoveredDevices.unshift({
            ...device,
            timestamp: Date.now()
          })
        }
      })

      this.discoveredDevices.sort((a, b) => (a.distance || 9999) - (b.distance || 9999))
    },

    updateDeviceStatus(deviceData) {
      const index = this.discoveredDevices.findIndex(d => d.deviceId === deviceData.deviceId)
      if (index !== -1) {
        this.$set(this.discoveredDevices, index, {
          ...this.discoveredDevices[index],
          pairStatus: deviceData.pairStatus,
          groupId: deviceData.groupId,
          deviceStatus: deviceData.deviceStatus,
          onlineStatus: deviceData.onlineStatus,
          voltageLevel: deviceData.voltageLevel,
          timestamp: Date.now()
        })
      }
    },

    getCurrentLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          this.$message.error('您的浏览器不支持地理定位')
          reject(new Error('浏览器不支持定位'))
          return
        }

        const loading = this.$message.info('正在获取您的位置...')

        navigator.geolocation.getCurrentPosition(
          (position) => {
            loading.close()
            const wgsLng = position.coords.longitude
            const wgsLat = position.coords.latitude
            const [gcjLat, gcjLng] = wgs84ToGcj02(wgsLat, wgsLng)

            this.currentLocation = {
              lng: gcjLng,
              lat: gcjLat,
              accuracy: position.coords.accuracy,
              locationType: '浏览器定位(WGS-84→GCJ-02)'
            }
            resolve(this.currentLocation)
          },
          (error) => {
            loading.close()

            switch (error.code) {
              case error.PERMISSION_DENIED:
                this.showLocationPermissionGuide()
                reject(new Error('定位权限被拒绝'))
                break
              case error.POSITION_UNAVAILABLE:
                this.$message.error('位置信息不可用，请检查GPS信号')
                reject(new Error('位置信息不可用'))
                break
              case error.TIMEOUT:
                this.$message.error('定位请求超时，请稍后再试')
                reject(new Error('定位请求超时'))
                break
              default:
                this.$message.error('定位失败，请稍后再试')
                reject(new Error('定位失败'))
                break
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        )
      })
    },

    generateRequestId() {
      return `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    },

    async startSearch() {
      if (!this.hasPairingPermission) {
        this.showPermissionGuide()
        return
      }

      if (this.searching || this.pairing) {
        this.$message.warning('正在搜索或绑定中，请稍后再试')
        return
      }

      if (!this.wsConnected) {
        this.$message.warning('WebSocket 未连接，请稍后再试')
        return
      }

      try {
        const location = await this.getCurrentLocation()
        if (!location.lng || !location.lat) {
          this.$message.error('无法获取当前位置')
          return
        }

        this.currentRequestId = this.generateRequestId()

        const searchCommand = {
          type: 'start_device_search',
          requestId: this.currentRequestId,
          timestamp: Date.now(),
          location: {
            lng: location.lng,
            lat: location.lat
          },
          searchRadius: this.formData.searchRadius,
          targetCount: this.formData.deviceCount
        }

        console.log('发送搜索指令:', searchCommand)

        this.wsService.send(searchCommand)

        this.searching = true
        this.pairing = false
        this.discoveredDevices = []

        this.$message.success(`开始搜索附近设备，目标${this.formData.deviceCount}台，范围${this.formData.searchRadius}米`)

      } catch (error) {
        console.error('开始搜索失败:', error)
        if (error.message !== '定位权限被拒绝') {
          this.$message.error('开始搜索失败：' + error.message)
        }
      }
    },

    stopAllTimers() {
      if (this.searchTimer) {
        clearInterval(this.searchTimer)
        this.searchTimer = null
      }
    },

    stopSearch(silent = false) {
      if (!this.searching && !silent) {
        this.$message.info('当前没有正在进行的搜索')
        return
      }

      this.stopAllTimers()

      if (this.wsService && this.wsService.isConnected && this.currentRequestId) {
        const stopCommand = {
          type: 'stop_device_search',
          requestId: this.currentRequestId,
          timestamp: Date.now()
        }
        console.log('发送停止指令:', stopCommand)
        this.wsService.send(stopCommand)
      }

      this.searching = false
      this.pairing = false

      if (!silent) {
        this.$message.info('已停止搜索')
      }
    },

    confirmBind() {
      if (!this.hasPairingPermission) {
        this.showPermissionGuide()
        return
      }

      if (!this.wsConnected) {
        this.$message.warning('WebSocket 未连接，请稍后再试')
        return
      }

      if (!this.canConfirmBind) {
        this.$message.warning(`需要至少${this.formData.deviceCount}台设备才能绑定，当前发现${this.discoveredDevices.length}台`)
        return
      }

      this.$confirm(`确定要绑定当前发现的${this.discoveredDevices.length}台设备吗？`, '确认绑定', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.pairing = true

        const bindCommand = {
          type: 'confirm_device_bind',
          requestId: this.generateRequestId(),
          timestamp: Date.now(),
          devices: this.discoveredDevices.map(d => ({
            deviceId: d.deviceId,
            batchNo: d.batchNo,
            distance: d.distance
          })),
          targetCount: this.formData.deviceCount,
          location: {
            lng: this.currentLocation.lng,
            lat: this.currentLocation.lat
          }
        }

        console.log('发送绑定指令:', bindCommand)

        if (this.wsService && this.wsService.isConnected) {
          this.wsService.send(bindCommand)
          this.$message.info('正在创建分组并下发指令，等待设备确认...')
        } else {
          this.$message.error('WebSocket未连接')
          this.pairing = false
        }
      }).catch(() => {})
    },

    getStatusText(status) {
      switch (status) {
        case '0': return '正常'
        case '1': return '报警'
        case '2': return '离线'
        default: return '未知'
      }
    },

    getStatusTagType(status) {
      switch (status) {
        case '0': return 'success'
        case '1': return 'danger'
        case '2': return 'info'
        default: return 'info'
      }
    },

    getOnlineStatusText(onlineStatus) {
      switch (onlineStatus) {
        case '0': return '单机'
        case '1': return '联机'
        default: return '未知'
      }
    },

    getOnlineStatusTagType(onlineStatus) {
      switch (onlineStatus) {
        case '0': return 'info'
        case '1': return 'success'
        default: return 'info'
      }
    },

    getPairStatusText(pairStatus) {
      switch (pairStatus) {
        case '0': return '已配对'
        case '1': return '配对中'
        default: return '未知'
      }
    },

    getPairStatusTagType(pairStatus) {
      switch (pairStatus) {
        case '0': return 'success'
        case '1': return 'warning'
        default: return 'info'
      }
    },

    debugWebSocket() {
      console.log('WebSocket状态:', {
        connected: this.wsConnected,
        serviceExists: !!this.wsService,
        socketExists: this.wsService && this.wsService.socket,
        readyState: this.wsService && this.wsService.socket ? this.wsService.socket.readyState : 'N/A',
        searching: this.searching,
        pairing: this.pairing,
        deviceCount: this.discoveredDevices.length
      })

      if (this.wsService && this.wsService.socket) {
        const states = ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED']
        console.log('连接状态:', states[this.wsService.socket.readyState])
      }
    }
  }
}
</script>

<style scoped>
.device-pairing-container {
  padding: 10px;
  background: #f0f2f5;
  height: 93.4vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.control-card,
.location-card,
.status-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.device-list-card {
  max-height: 48vh;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-top: 15px;
  min-height: 0;
}

.device-list-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
  min-height: 0;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
  position: relative;
  min-height: 0;
}

.table-wrapper >>> .el-table {
  display: flex;
  flex-direction: column;
}

.table-wrapper >>> .el-table__body-wrapper {
  overflow-y: auto !important;
}

.location-info {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.location-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.location-item .label {
  color: #666;
  font-weight: normal;
}

.location-item .value {
  color: #333;
  font-weight: 500;
}

.text-success {
  color: #67c23a;
  font-weight: 500;
}

.el-table >>> .cell {
  font-size: 13px;
}
</style>
